/**
 * Static knockout bracket (FotMob-style): a mirrored single-elimination tree where both halves of
 * the draw meet at the Final in the centre, with the champion trophy above and the bronze-final
 * below. No gestures — the tree is horizontally scrollable so it works at any width. Source-
 * agnostic: it renders a normalized `KnockoutBracketModel`, so the league page and the World Cup
 * standings modal share one look.
 */

import React, { useCallback, useMemo, useState } from 'react'
import { View, Text, ScrollView, useWindowDimensions, type LayoutChangeEvent } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import type { BracketRound, KnockoutBracketModel } from '../../../src/knockout/types/knockoutBracket.types'
import { buildMirroredBracket } from '../../../src/knockout/helpers/mirrorBracket.helper'
import { fillConnectorWidth, trackHeight } from '../../../src/knockout/helpers/bracketGeometry.helper'
import { BRACKET_BOX_SLOT, BRACKET_BOX_W } from './knockoutBracket.constants'
import { BracketConnector } from './BracketConnector'
import { BracketRoundColumn } from './BracketRoundColumn'
import { BracketCenter } from './BracketCenter'
import { getKnockoutBracketStyles } from './KnockoutBracket.styles'

interface KnockoutBracketProps {
  model: KnockoutBracketModel
  testID?: string
}

export const KnockoutBracket: React.FC<KnockoutBracketProps> = ({ model, testID }) => {
  const theme = useTheme()
  const styles = useMemo(() => getKnockoutBracketStyles(theme), [theme])
  const { width: windowWidth } = useWindowDimensions()
  const [containerW, setContainerW] = useState(windowWidth)

  const onRootLayout = useCallback((e: LayoutChangeEvent) => {
    const w = Math.round(e.nativeEvent.layout.width)
    if (w > 0) setContainerW((prev) => (prev === w ? prev : w))
  }, [])

  const mirrored = useMemo(() => buildMirroredBracket(model), [model])

  const outerCount = mirrored.left.length > 0 ? mirrored.left[0].matches.length : 1
  const trackH = trackHeight(outerCount, BRACKET_BOX_SLOT)
  const isEmpty = mirrored.left.length === 0 && mirrored.final == null

  // Grow the column gaps so the tree fills the container width edge-to-edge on big screens (left
  // half pinned left, right half pinned right). On a narrow screen the gaps stay at their minimum
  // and the tree scrolls horizontally instead.
  const roundColumns = mirrored.left.length + mirrored.right.length
  const connectorCount =
    (mirrored.left.length > 0 ? mirrored.left.length : 0) +
    (mirrored.right.length > 0 ? mirrored.right.length : 0)
  const centerW = BRACKET_BOX_W + 24
  const connectorW = fillConnectorWidth(containerW, roundColumns, connectorCount, centerW)

  // Left half: outer → inner, each round followed by the elbow into the next (then a straight line
  // into the centre). The centre-side count drives each connector's child count.
  const leftPieces = useMemo<React.ReactNode[]>(() => {
    const pieces: React.ReactNode[] = []
    mirrored.left.forEach((round: BracketRound, i) => {
      pieces.push(<BracketRoundColumn key={`l-col-${round.key}`} round={round} trackH={trackH} />)
      const next = mirrored.left[i + 1]
      if (next != null) {
        pieces.push(
          <BracketConnector key={`l-con-${round.key}`} trackH={trackH} childCount={next.matches.length} side="left" connectorW={connectorW} />,
        )
      }
    })
    if (mirrored.left.length > 0) {
      pieces.push(<BracketConnector key="l-con-center" trackH={trackH} childCount={1} side="left" straight connectorW={connectorW} />)
    }
    return pieces
  }, [mirrored.left, trackH, connectorW])

  // Right half: a straight line out of the centre, then inner → outer (reverse of `right`), each
  // round followed by the elbow gathering the next (outer) round into it.
  const rightPieces = useMemo<React.ReactNode[]>(() => {
    const reversedRight = [...mirrored.right].reverse()
    const pieces: React.ReactNode[] = []
    if (mirrored.right.length > 0) {
      pieces.push(<BracketConnector key="r-con-center" trackH={trackH} childCount={1} side="right" straight connectorW={connectorW} />)
    }
    reversedRight.forEach((round: BracketRound, j) => {
      pieces.push(<BracketRoundColumn key={`r-col-${round.key}`} round={round} trackH={trackH} />)
      if (j < reversedRight.length - 1) {
        pieces.push(
          <BracketConnector key={`r-con-${round.key}`} trackH={trackH} childCount={round.matches.length} side="right" connectorW={connectorW} />,
        )
      }
    })
    return pieces
  }, [mirrored.right, trackH, connectorW])

  return (
    <View style={styles.root} testID={testID} onLayout={onRootLayout}>
      {isEmpty ? (
        <Text style={styles.emptyText}>
          The knockout bracket will appear once fixtures are scheduled.
        </Text>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.treeContent}
        >
          <View style={styles.row}>
            {leftPieces}
            <BracketCenter trackH={trackH} final={mirrored.final} thirdPlace={mirrored.thirdPlace} />
            {rightPieces}
          </View>
        </ScrollView>
      )}
    </View>
  )
}
